import { Fragment, useEffect } from "react"
import { bool, string } from "prop-types"
import { Link, Navigate, Route, Routes, useNavigate } from "react-router-dom"
import { useSession } from "source/shared/hooks/useSession"
import {
  Icon,
  BreadcrumbNavigationWithMe,
  BreadcrumbPage,
  BreadcrumbDividerIcon,
} from "source/shared/components"
import { useApiRead } from "source/shared/SessionApiResource"
import { EditProfileAndHousehold } from "./EditProfileAndHousehold"
import { ProfileAvatar } from "./ProfileAvatar"
import { Heading } from "@planningcenter/doxy-web"
import { useAllSchools } from "./hooks/useAllSchools"
import { Profile } from "source/myChurchCenter/ProfileAndHousehold/Profile"
import { ProfileSelect } from "source/myChurchCenter/ProfileAndHousehold/ProfileSelect"
import { useProfile } from "source/myChurchCenter/ProfileAndHousehold/hooks/useProfile"
import { useCustomFields } from "source/myChurchCenter/ProfileAndHousehold/hooks/useCustomFields"
import { useHouseholds } from "source/myChurchCenter/ProfileAndHousehold/hooks/useHouseholds"
import { useSearchParams } from "../hooks/useSearchParams"
import { css } from "@emotion/react"
import { useFlipperFeatureEnabled } from "source/shared/flipperFeatures"

const MARITAL_STATUS_OPTIONS_PATH = "/people/v2/marital_statuses"

const CAMPUS_PATH = "/people/v2/campuses?per_page=100"

const MEMBERSHIPS_PATH = "/people/v2/membership_statuses?per_page=100"

export function ProfileAndHousehold() {
  const {
    data: { id: currentPersonId },
  } = useSession(true)

  const [searchParams, setSearchParams] = useSearchParams()
  const profileId = searchParams.get("profile")
  const householdId = searchParams.get("household")

  const { data: currentUserProfile } = useProfile(currentPersonId)
  const currentUserIsChild = currentUserProfile.data.attributes.child
  const canEditHouseholdMemberInfo =
    !currentUserIsChild || currentPersonId === profileId

  const { data: households } = useHouseholds()
  const navigate = useNavigate()

  useEffect(() => {
    if (households.length > 1 && !householdId) {
      return navigate("/households", { replace: true })
    } else if (households.length === 1 && !householdId) {
      // the shared link copied from the CAPEM doesn't have a household param
      // which is ok when we have multiple households but profiles with only one
      // end up showing up without household member roles
      setSearchParams((s) => {
        s.set("household", households[0].id)
      })
    }

    if (!profileId) {
      setSearchParams((s) => {
        s.set("profile", currentPersonId)

        return s
      })
    }
  }, [
    currentPersonId,
    navigate,
    householdId,
    households,
    profileId,
    setSearchParams,
  ])

  if (!searchParams.get("profile")) {
    return <div>Loading...</div>
  }

  return (
    <CurrentProfileAndHousehold
      profileId={profileId}
      currentUserIsChild={currentUserIsChild}
      canEditHouseholdMemberInfo={canEditHouseholdMemberInfo}
    />
  )
}

CurrentProfileAndHousehold.propTypes = {
  profileId: string.isRequired,
  canEditHouseholdMemberInfo: bool.isRequired,
  currentUserIsChild: bool.isRequired,
}
function CurrentProfileAndHousehold({
  profileId,
  canEditHouseholdMemberInfo,
  currentUserIsChild,
}) {
  const parentsGuardiansEnabled = useFlipperFeatureEnabled(
    "ROLLOUT_parents_and_guardians",
  )

  const navigate = useNavigate()
  // TODO useNarrowLayout()
  const profilePath = `/people/v2/people/${profileId}?include=emails,phone_numbers,addresses.country,marital_status,school&fields[Country]=address_fields,name`
  const { data: profile, refetch: reloadProfile } = useProfile(profileId)
  const { data: customFields, refetch: reloadCustomFields } =
    useCustomFields(profileId)

  const maritalStatusOptions = useApiRead(MARITAL_STATUS_OPTIONS_PATH)

  const schoolOptions = useAllSchools()

  const campuses = useApiRead(CAMPUS_PATH)
  const memberships = useApiRead(MEMBERSHIPS_PATH)

  const name = profile.data.attributes.name
  const avatar =
    profile.data.attributes.avatar_url ||
    profile.data.attributes.demographic_avatar_url
  const includedRecords = profile.included
  const canEditContactInfo =
    profile.data.attributes.contact_info_editable_by_household ||
    profileId === profile.data.id

  const params = new URLSearchParams(window.location.search)
  const { data: households, refetch: reloadHouseholds } = useHouseholds()
  const currentHousehold = households.find(
    (hh) => hh.id === params.get("household"),
  )
  const currentHouseholdMember = currentHousehold?.householdMembers?.find(
    (hhm) => hhm.personId === profileId,
  )

  const hasOtherHouseholdMembers =
    currentHousehold?.householdMembers?.length > 1

  const personalAttributesRecords = [
    "Anniversary",
    "Birthdate",
    "Child",
    "Gender",
    "Grade",
    "Graduation_Year",
    "Medical_Notes",
    "Membership",
  ].map((recordType) => {
    return {
      id: recordType,
      attributes: {
        [recordType.toLowerCase()]:
          profile.data.attributes[recordType.toLowerCase()],
      },
      type: recordType,
      links: { self: profile.data.links.self },
    }
  })

  const addressCountries = profile.included.filter(
    (record) => record.type === "Country",
  )

  // NOTE: We need a default state for MaritalStatus in the event we don't have one
  // in the `included` fields.
  if (includedRecords.every((record) => record.type !== "MaritalStatus")) {
    const defaultMaritalStatus = {
      type: "MaritalStatus",
      id: "",
      attributes: {
        marital_status_id: "",
      },
      links: {
        self: "",
      },
    }

    includedRecords.push(defaultMaritalStatus)
  }

  if (includedRecords.every((record) => record.type !== "SchoolOption")) {
    const defaultSchool = {
      type: "SchoolOption",
      id: "",
      attributes: {
        school_id: "",
      },
      links: {
        self: "",
      },
    }

    includedRecords.push(defaultSchool)
  }

  if (includedRecords.every((record) => record.type !== "Campus")) {
    const defaultCampus = {
      type: "Campus",
      id: "",
      attributes: {
        primary_campus_id: "",
      },
      links: {
        self: profile.data.links.self,
      },
    }

    includedRecords.push(defaultCampus)
  }

  function mapRecordsFieldsForPatchingToPersonRelationship(records) {
    const membershipId = memberships.data.find(
      (m) => m.attributes.value === profile.data.attributes.membership,
    )?.id

    return records.map((record) => {
      switch (record.type) {
        case "MaritalStatus":
          record.links.self = profile.data.links.self
          record.attributes.marital_status_id = record.id
          record.deleteValueAttributeForPatch = true
          break
        case "SchoolOption":
          record.links.self = profile.data.links.self
          record.attributes.school_id = record.id
          record.deleteValueAttributeForPatch = true
          break
        case "Campus":
          record.links.self = profile.data.links.self
          record.attributes.primary_campus_id = record.id
          break
        case "Membership":
          record.id = membershipId
          record.attributes = {
            membership_status_id: record.id,
            value: record.attributes.membership,
          }
          record.deleteValueAttributeForPatch = true
          break
      }

      return record
    })
  }

  const householdMembershipRecord = {
    id: "HouseholdMember",
    attributes: {
      household_role: currentHouseholdMember?.householdRole,
    },
    type: "HouseholdMember",
    links: { self: currentHouseholdMember?.link },
  }

  const records = [
    ...mapRecordsFieldsForPatchingToPersonRelationship(includedRecords),
    ...mapRecordsFieldsForPatchingToPersonRelationship(
      personalAttributesRecords,
    ),
    ...customFields.data,
    ...(parentsGuardiansEnabled ? [householdMembershipRecord] : []),
  ]

  const returnUri = new URL(
    `/me/profile-and-household?profile=${profileId}`,
    window.location.origin,
  )
  if (params.get("household")) {
    returnUri.searchParams.set("household", params.get("household"))
  }

  function onDiscard() {
    navigate(returnUri.pathname + returnUri.search)
  }

  function onSave() {
    reloadProfile()
    reloadCustomFields()
    reloadHouseholds()
    navigate(returnUri.pathname + returnUri.search)
  }

  function householdRoleDescription(role) {
    switch (role) {
      case "parent_guardian":
        return "Parent/guardian"
      case "other_adult":
      case "adult":
        return "Adult"
      case "child_or_dependent":
        return "Child"
      default:
        break
    }
  }

  function updateAdultHouseholdRole() {
    if (!currentHousehold) return null

    const otherMembers = currentHousehold.householdRoles.filter(
      (hhr) => hhr.id !== currentHouseholdMember.householdMemberId,
    )
    const children = otherMembers.filter(
      (hhr) => hhr.household_role === "child_or_dependent",
    )
    const parents = otherMembers.filter(
      (hhr) => hhr.household_role === "parent_guardian",
    )

    return parents.length || children.length ? "other_adult" : "adult"
  }

  function allOtherParents() {
    if (!currentHouseholdMember) return false

    const otherMembers = currentHousehold.householdRoles.filter(
      (hhr) => hhr.id !== currentHouseholdMember.householdMemberId,
    )

    return otherMembers.every(
      (member) => member.household_role === "parent_guardian",
    )
  }

  return (
    <Fragment>
      <BreadcrumbNavigationWithMe>
        <BreadcrumbPage
          linkUrl="/me/profile"
          pageText="My profile & settings"
        />
        <BreadcrumbDividerIcon />
        {households.length > 1 && (
          <>
            <BreadcrumbPage linkUrl="/households" pageText="Households" />
            <BreadcrumbDividerIcon />
          </>
        )}
        <BreadcrumbPage
          pageText="Profile &amp; household information"
          isActive
        />
      </BreadcrumbNavigationWithMe>

      <Routes>
        <Route
          path="/"
          element={
            <ProfileSelect
              key={profileId}
              profileId={profileId}
              canAddHouseholdMember={!currentUserIsChild}
            />
          }
        />
      </Routes>

      <div className="d-f ai-c mb-2">
        <ProfileAvatar
          avatar={avatar}
          canEditHouseholdMemberInfo={canEditHouseholdMemberInfo}
          hasCustomAvatar={!!profile.data.attributes.avatar_url}
          name={name}
          personPath={profilePath}
          profileId={profileId}
          reloadProfile={reloadProfile}
        />
        <div className="d-f fd-c">
          <Heading level={1} size={2} text={name} />
          {parentsGuardiansEnabled && currentHouseholdMember && (
            <span className="fw-600 c-tint2">
              {householdRoleDescription(currentHouseholdMember.householdRole)}
            </span>
          )}
        </div>
        {households.length === 0 && (
          <div className="ml-a">
            <Link
              className="btn fs-1 p-0"
              css={css`
                aspect-ratio: 1;
              `}
              to="/households/new"
              title="Create a new household"
            >
              <Icon symbol="general#plus" aria-hidden />
            </Link>
          </div>
        )}
      </div>
      <Routes>
        <Route
          path="edit"
          element={
            <EditProfileAndHousehold
              canEditContactInfo={canEditContactInfo}
              editableOptions={{
                maritalStatusOptions: maritalStatusOptions.data,
                schoolOptions: schoolOptions,
                campusOptions: campuses.data,
                membershipOptions: memberships.data,
              }}
              isChild={profile.data.attributes.child}
              onDiscard={onDiscard}
              onSave={onSave}
              personId={profile.data.id}
              records={records}
              addressCountries={addressCountries}
              householdName={currentHousehold?.householdName}
              onHouseholdRoleChange={updateAdultHouseholdRole}
              hasOtherHouseholdMembers={hasOtherHouseholdMembers}
              allOtherParents={allOtherParents()}
            />
          }
        />
        <Route
          path="/"
          element={
            <Profile
              personId={profileId}
              records={records}
              isChild={profile.data.attributes.child}
              canEditHouseholdMemberInfo={canEditHouseholdMemberInfo}
              householdName={currentHousehold?.householdName}
              householdMemberRole={householdRoleDescription(
                currentHouseholdMember?.householdRole,
              )}
            />
          }
        />
        <Route path="*" element={<Navigate to="/me/profile" replace />} />
      </Routes>
    </Fragment>
  )
}
